import { FC } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { FavoriteHeart } from 'pages/avia/favorites/components/favorites-heart'
import { FavoritesWarning } from 'pages/avia/favorites/components/favorites-warning'
import { useFavorites } from 'pages/avia/favorites/hooks'

import { DifficultRoute } from 'entities/ticket/models/find-ticket/models'
import { AviaTicketTags } from 'entities/ticket/ui/card-of-ticket/module/model.ts'
import { FlightInfo } from 'entities/ticket/ui/card-of-ticket/ui/components/flight-info'
import { Indicators } from 'entities/ticket/ui/card-of-ticket/ui/components/indicators'

import { FavoriteParams } from 'shared/api/avia/favorites'
import { priceShow } from 'shared/lib//priceShow'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types.ts'
import { Border } from 'shared/ui/border'
import { ImageLazy } from 'shared/ui/image-lazy'
import { MBonusIcons } from 'shared/ui/mbonus/mbonus-icons'
import { TagPayment } from 'shared/ui/mplus/tag-payment'
import { Paper } from 'shared/ui/paper'
import { TagTicket } from 'shared/ui/tag-ticket/tag-ticket.tsx'

import { Carrier, ICardItem } from '../module'

import s from './styles.module.scss'

interface Props {
  index?: number
  ticket: ICardItem
  isMbonus?: boolean
  isFavorites?: boolean
  showWarning?: boolean
  searchParams: FavoriteParams
  directions?: DifficultRoute[]
  isSavedTicket?: boolean
  onClick?: () => void
}

export const ShowInfoCard = ({
  title,
  subTitle,
}: {
  title?: string
  subTitle?: string
}) => {
  return (
    <div className={s.itemContainer}>
      <p className={s.title}>{title}</p>
      <p className={s.subTitle}>{subTitle}</p>
    </div>
  )
}

export const ShowCarrier: FC<{ carrier?: Carrier[] }> = ({ carrier }) => {
  if ((carrier?.length || 0) <= 2) {
    return (
      <div className={s.wrapperCarrier}>
        {carrier?.map((carrier, index) => {
          return (
            <img
              src={carrier?.image}
              key={index}
              alt=""
            />
          )
        })}
      </div>
    )
  } else {
    return (
      <div className={s.wrapperAdditional}>
        <img
          src={carrier?.[0]?.image}
          alt=""
        />
        <div className={s.empty}>+{(carrier?.length || 0) - 1}</div>
      </div>
    )
  }
}

export const CardOfTicket: FC<Props> = ({
  index,
  ticket,
  onClick,
  isFavorites,
  searchParams,
  isSavedTicket,
  directions,
}) => {
  const { t } = useTranslation()

  const favoritesParams = {
    params: searchParams,
    directions,
  }

  const { onAddFavorite, onRemoveFavorite } = useFavorites({
    favoritesSearchKey: ticket?.favorite_search_key,
    favoritesParams,
  })

  const { adults = 0, children = 0, infants = 0 } = ticket?.search_params || {}
  const passengersCount = adults + children + infants

  const price = String(ticket.mplus_amount || ticket?.price)
  const firstSegment = ticket?.segments?.[0]

  const tag = ticket?.tag && AviaTicketTags[ticket.tag]

  return (
    <div
      id={`ticket-card-${index}`}
      className={clsx(s.container, isSavedTicket && s.selectedTicket)}
      onClick={onClick}
    >
      <Paper
        padding={[16]}
        radius={20}
      >
        <div>
          <div className={s.header}>
            <div className={s.top}>
              <div className={s.left}>
                <div className={s.priceBox}>
                  <p className={clsx(s.price, 'extra-bold-20')}>
                    {priceShow(price)} &#8384;
                  </p>

                  {ticket?.old_price && (
                    <p className={clsx(s.oldPrice, 'medium-14')}>
                      {priceShow(ticket?.old_price)} &#8384;
                    </p>
                  )}
                </div>

                {/* {ticket?.old_price && (
                  <p className={clsx(s.oldPrice, 'semi-bold-12')}>
                    {priceShow(ticket?.old_price)} &#8384;
                  </p>
                )} */}
              </div>

              <FavoriteHeart
                onAdd={onAddFavorite}
                onRemove={onRemoveFavorite}
                defaultActive={ticket?.is_favorite}
              />
            </div>

            <div className={s.tagBox}>
              {ticket?.is_fare_family && (
                <TagTicket
                  title={t(AviaTicketTags.FARE_FAMILY.title)}
                  bgColor={AviaTicketTags.FARE_FAMILY.color}
                  icon={AviaTicketTags.FARE_FAMILY.icon}
                />
              )}

              {tag && (
                <TagTicket
                  title={t(tag?.title)}
                  bgColor={tag?.color}
                  icon={tag?.icon}
                />
              )}

              {ticket?.is_bonus && ticket?.bonus_amount !== undefined && (
                <MBonusIcons
                  text={t('tickets_page.mbonus_amount', {
                    amount: priceShow(ticket?.bonus_amount),
                  })}
                />
              )}

              <TagPayment
                price={ticket.price}
                tranche={ticket?.monthly_tranche}
                mInstallmentInfo={ticket?.mInstallmentInfo}
              />

              {ticket?.freeSeat && (
                <div className={clsx('semi-bold-11', s.freeSeat)}>
                  <AppIcon
                    id={AllIcons.SEATS}
                    width={10}
                    height={10}
                  />
                  {t('count_seat', { count: ticket?.freeSeat })}
                </div>
              )}
            </div>
          </div>

          <Border />

          <div className={s.segments}>
            {ticket?.segments?.map((item) => {
              return (
                <div className={s.segmentBox}>
                  <div className={clsx('medium-12', s.airlines)}>
                    <ImageLazy
                      src={item?.carrier?.[0].image}
                      className={s.icon}
                    />
                    {item?.carrier?.[0].name}
                  </div>

                  <FlightInfo data={item} />

                  <div className={s.indicatorWrapper}>
                    <Indicators baggageInfo={item?.baggageInfo} />
                  </div>

                  <Border />
                </div>
              )
            })}
          </div>

          {isFavorites && (
            <div>
              <Border />

              <FavoritesWarning
                outboundDate={firstSegment?.from?.date}
                passengersCount={passengersCount}
                isTimeLeft={ticket?.is_30_minutes_left}
              />
            </div>
          )}
        </div>
      </Paper>
    </div>
  )
}
